/*
 * School Admin Settings (Layer 4 of the 5/30/2026 hardening pack).
 *
 * Mounted at /diagnostic/settings. Restricted to tenant_admin / admin
 * (gate enforced both client-side via getSession and server-side via the
 * tenant-settings router). Three tabs:
 *   1. User Profiles  — CRUD on tenant_users (invite, edit role/name,
 *      reset password, deactivate / reactivate).
 *   2. Submission Date — sets `annual_submission_date` (ISO date).
 *   3. Reminders       — toggle, configurable offsets (6w/4w/2w/1w/1d),
 *      recipient email list. Includes a manual "Run reminder scan" button.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Shell } from "./CaporilliWorkspace";
import {
  Users, Calendar, BellRing, ShieldCheck, KeyRound, UserPlus, Trash2, RefreshCcw,
  Mail, FileSearch, Download, SlidersHorizontal, FlaskConical, History as HistoryIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getSession = () => { try { return JSON.parse(localStorage.getItem("diag_session") || "null"); } catch { return null; } };
const getHdrs = () => {
  const s = getSession();
  if (!s?.user?.id || !s?.tenant?.id) return null;
  return { "x-tenant-id": s.tenant.id, "x-user-id": s.user.id, "x-user-role": s.user.role || "tenant_admin" };
};

const TABS = [
  { key: "features", label: "Diagnostic", icon: SlidersHorizontal },
  { key: "date", label: "Submission Date", icon: Calendar },
  { key: "reminders", label: "Reminders", icon: BellRing },
  { key: "audit", label: "Audit", icon: FileSearch },
];

const TenantSettingsPage = () => {
  const navigate = useNavigate();
  const session = getSession();
  const [tab, setTab] = useState("features");

  useEffect(() => {
    if (!session) { navigate("/diagnostic/login", { replace: true }); return; }
    if (!["tenant_admin", "admin", "super_admin"].includes(session.user?.role)) {
      toast.error("Settings are restricted to tenant admins.");
      navigate("/diagnostic", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Shell>
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="tenant-settings-page">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="h-6 w-6 text-[#3B4FE4]" />
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">School Admin Settings</h1>
            <p className="text-[13px] text-[#6b7280]">Manage the annual submission date and reminder cadence.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#e5e7eb] mb-6 flex gap-1 overflow-x-auto" data-testid="tenant-settings-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 inline-flex items-center gap-2 text-[13px] font-semibold border-b-2 transition-colors ${
                  active
                    ? "border-[#3B4FE4] text-[#3B4FE4]"
                    : "border-transparent text-[#6b7280] hover:text-[#111827]"
                }`}
                data-testid={`tenant-settings-tab-${t.key}`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "features" && <FeaturesTab />}
        {tab === "date" && <SubmissionDateTab />}
        {tab === "reminders" && <RemindersTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </Shell>
  );
};

/* ── Tab: Diagnostic features (What-If, History, Approval mode) ── */
const FeaturesTab = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const h = getHdrs(); if (!h) return;
    axios.get(`${API}/api/tenant-settings/features`, { headers: h })
      .then((r) => setF(r.data.features))
      .catch(() => toast.error("Failed to load feature settings"));
  }, []);

  const save = async (next) => {
    const h = getHdrs(); if (!h) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/tenant-settings/features`, next, { headers: h });
      setF(r.data.features);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!f) return <div className="text-[13px] text-[#6b7280]" data-testid="features-loading">Loading…</div>;

  const Toggle = ({ on, onClick, testid }) => (
    <button type="button" onClick={onClick} disabled={saving} data-testid={testid} aria-pressed={on}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? "bg-[#059669]" : "bg-[#cbd5e1]"} disabled:opacity-60`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );

  const Row = ({ icon: Icon, title, desc, children }) => (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-[#e5e7eb] last:border-0">
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="h-5 w-5 text-[#475569] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-[#111827]">{title}</div>
          <div className="text-[12px] text-[#6b7280] mt-0.5">{desc}</div>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white p-5 max-w-[720px]" data-testid="features-tab">
      <Row icon={FlaskConical} title="What-If Simulator"
        desc="Let staff model how potential improvements could change the score. Disabling hides it everywhere; saved scenarios are kept.">
        <Toggle on={f.whatif_enabled} testid="feature-whatif-toggle"
          onClick={() => save({ ...f, whatif_enabled: !f.whatif_enabled })} />
      </Row>
      <Row icon={HistoryIcon} title="Diagnostic History"
        desc="Show past approved diagnostics and the trend chart. Disabling hides it and blocks ordinary users from retrieving history; records are preserved for admins/audit.">
        <Toggle on={f.history_enabled} testid="feature-history-toggle"
          onClick={() => save({ ...f, history_enabled: !f.history_enabled })} />
      </Row>
      <div className="pt-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-[#475569] mt-0.5 shrink-0" />
          <div className="min-w-0 w-full">
            <div className="text-[14px] font-semibold text-[#111827]">Submission approval</div>
            <div className="text-[12px] text-[#6b7280] mt-0.5 mb-3">
              Choose how a Diagnostic is finalized. Changing this only affects Diagnostics submitted afterwards.
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { v: "single", t: "Single-Level Approval", d: "Completion & submission by an authorized user finalizes it immediately — no second approver." },
                { v: "secondary", t: "Secondary-Level Approval", d: "Submission enters “Awaiting Approval”; a different School Admin must approve before it is Final." },
              ].map((opt) => {
                const active = f.approval_mode === opt.v;
                return (
                  <button key={opt.v} type="button" disabled={saving}
                    onClick={() => save({ ...f, approval_mode: opt.v })}
                    data-testid={`feature-approval-${opt.v}`}
                    className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-60 ${active ? "border-[#3B4FE4] bg-[#eff6ff] ring-1 ring-[#3B4FE4]" : "border-[#e5e7eb] hover:bg-[#f9fafb]"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`h-3.5 w-3.5 rounded-full border-2 ${active ? "border-[#3B4FE4] bg-[#3B4FE4]" : "border-[#cbd5e1]"}`} />
                      <span className="text-[13px] font-semibold text-[#111827]">{opt.t}</span>
                    </div>
                    <div className="text-[12px] text-[#6b7280] mt-1 pl-5.5">{opt.d}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Tab 1: Users ── */
export const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user object
  const [invite, setInvite] = useState({ email: "", full_name: "", role: "contributor", title: "" });
  const [busy, setBusy] = useState(false);
  const [seatUsage, setSeatUsage] = useState(null);
  const [seatModalOpen, setSeatModalOpen] = useState(false);
  const [seatRequestQty, setSeatRequestQty] = useState(1);
  const [seatRequestBusy, setSeatRequestBusy] = useState(false);
  const session = getSession();

  const loadSeats = async () => {
    try {
      const r = await axios.get(`${API}/api/license/caporilli_diagnostic/me`, { headers: getHdrs() });
      setSeatUsage(r.data?.seat_usage || null);
    } catch { /* silent */ }
  };

  const load = async () => {
    const h = getHdrs(); if (!h) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/diagnostics/tenant/users`, { headers: h });
      setUsers(r.data || []);
    } catch (e) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadSeats(); /* eslint-disable-next-line */ }, []);

  const submitInvite = async () => {
    if (!invite.email || !invite.full_name) return toast.error("Email and full name are required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/diagnostics/tenant/users`, invite, { headers: getHdrs() });
      toast.success(`Invited ${r.data?.user?.email}. Temp password: ${r.data?.temp_password}`, { duration: 30000 });
      setInvite({ email: "", full_name: "", role: "contributor", title: "" });
      load(); loadSeats();
    } catch (e) {
      // Phase 1: seat-limit modal (HTTP 402 with seat_usage payload)
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.error === "seat_limit_reached") {
        setSeatUsage(d.seat_usage);
        setSeatModalOpen(true);
      } else {
        toast.error(d || "Invite failed");
      }
    }
    finally { setBusy(false); }
  };

  const requestSeats = async () => {
    setSeatRequestBusy(true);
    try {
      const r = await axios.post(`${API}/api/license/caporilli_diagnostic/seat-request`,
        { additional_seats: seatRequestQty, note: "Requested from Settings → Users" },
        { headers: getHdrs() });
      toast.success(r.data?.message || "Seat request recorded", { duration: 15000 });
      setSeatModalOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setSeatRequestBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/api/tenant-settings/users/${editing.id}`,
        { full_name: editing.full_name, role: editing.role, title: editing.title || "" },
        { headers: getHdrs() });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const r = await axios.post(`${API}/api/diagnostics/tenant/users/${u.id}/reset-password`, {}, { headers: getHdrs() });
      toast.success(`Reset. Temp password: ${r.data?.temp_password}`, { duration: 30000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const deactivate = async (u) => {
    if (u.id === session?.user?.id) return toast.error("Cannot deactivate yourself.");
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/api/diagnostics/tenant/users/${u.id}`, { headers: getHdrs() });
      toast.success("User deactivated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reactivate = async (u) => {
    try {
      await axios.post(`${API}/api/tenant-settings/users/${u.id}/reactivate`, {}, { headers: getHdrs() });
      toast.success("User reactivated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [emailEdit, setEmailEdit] = useState(null); // user being edited
  const [newEmail, setNewEmail] = useState("");
  const submitEmailChange = async () => {
    if (!emailEdit) return;
    try {
      await axios.post(`${API}/api/tenant-settings/users/${emailEdit.id}/change-email`,
        { email: newEmail.trim().toLowerCase() }, { headers: getHdrs() });
      toast.success("Email updated");
      setEmailEdit(null); setNewEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Email change failed"); }
  };

  return (
    <div className="space-y-6" data-testid="tenant-settings-users-tab">
      {/* Seat usage banner */}
      {seatUsage && (
        <div className="rounded-xl border border-[#e5e7eb] bg-white p-4 flex items-center justify-between gap-3" data-testid="ts-seat-usage">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">License Seats (License Year {seatUsage.license_year})</div>
            <div className="text-[14px] font-semibold text-[#111827] mt-0.5">
              <span data-testid="ts-seat-used">{seatUsage.active_users}</span> / <span data-testid="ts-seat-total">{seatUsage.total_allowed_user_seats}</span> active users
              <span className="text-[12px] text-[#6b7280] font-normal ml-2">
                · {seatUsage.included_user_seats} included
                {seatUsage.additional_user_seats > 0 ? ` + ${seatUsage.additional_user_seats} additional` : ""}
              </span>
            </div>
          </div>
          {seatUsage.seats_remaining === 0 && seatUsage.seat_limit_enforced && (
            <Button onClick={() => setSeatModalOpen(true)}
              className="bg-[#0b1220] hover:bg-[#1f2937] text-white"
              data-testid="ts-request-seats-btn">
              Request more seats
            </Button>
          )}
        </div>
      )}

      {/* Seat-limit modal */}
      {seatModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSeatModalOpen(false)} data-testid="ts-seat-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#111827]">Add additional seats</h3>
            <p className="text-[13px] text-[#6b7280] mt-2">
              Your annual MySchoolRx license includes <b>{seatUsage?.included_user_seats || 5} users</b>. Additional users are <b>${seatUsage?.additional_user_price || 500}</b> per user for the current license year.
            </p>
            <div className="mt-4 flex items-center gap-3">
              <label className="text-[12px] font-semibold text-[#374151]">Additional seats</label>
              <Input type="number" min="1" max="50" value={seatRequestQty}
                onChange={(e) => setSeatRequestQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 bg-white border-[#e5e7eb] text-[14px]"
                data-testid="ts-seat-qty" />
              <span className="text-[13px] text-[#6b7280]">
                = ${(seatUsage?.additional_user_price || 500) * seatRequestQty}
              </span>
            </div>
            <p className="text-[11px] text-[#6b7280] mt-3">
              Payment is processed manually right now — our team will follow up to confirm and activate the additional seats.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSeatModalOpen(false)} className="border-[#e5e7eb]" data-testid="ts-seat-cancel">Cancel</Button>
              <Button onClick={requestSeats} disabled={seatRequestBusy} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="ts-seat-submit">
                {seatRequestBusy ? "Sending…" : "Request seats"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite form */}
      <section className="rounded-xl border border-[#e5e7eb] bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="h-5 w-5 text-[#3B4FE4]" />
          <h2 className="font-bold text-[#111827]">Invite a user</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1.5fr_1fr_1fr_auto] gap-3">
          <Input placeholder="Email" value={invite.email}
            onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))}
            className="bg-white border-[#e5e7eb] text-[13px]" data-testid="ts-invite-email" />
          <Input placeholder="Full name" value={invite.full_name}
            onChange={(e) => setInvite((p) => ({ ...p, full_name: e.target.value }))}
            className="bg-white border-[#e5e7eb] text-[13px]" data-testid="ts-invite-name" />
          <Input placeholder="Title (optional)" value={invite.title}
            onChange={(e) => setInvite((p) => ({ ...p, title: e.target.value }))}
            className="bg-white border-[#e5e7eb] text-[13px]" data-testid="ts-invite-title" />
          <select value={invite.role}
            onChange={(e) => setInvite((p) => ({ ...p, role: e.target.value }))}
            className="h-10 rounded-md border border-[#e5e7eb] bg-white px-2 text-[13px]"
            data-testid="ts-invite-role">
            <option value="contributor">Contributor</option>
            <option value="tenant_admin">School Admin</option>
          </select>
          <Button disabled={busy} onClick={submitInvite}
            className="bg-[#007a9e] hover:bg-[#00607d] text-white h-10" data-testid="ts-invite-submit">
            Invite
          </Button>
        </div>
        <p className="text-[11px] text-[#6b7280] mt-2">Temporary password is generated and displayed on success. Email delivery is currently MOCKED to the notification log.</p>
      </section>

      {/* Users table */}
      <section className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#e5e7eb] flex items-center gap-2">
          <Users className="h-4 w-4 text-[#6b7280]" />
          <h2 className="font-bold text-[#111827]">Users ({users.length})</h2>
        </div>
        {loading ? (
          <div className="p-6 text-[13px] text-[#6b7280]">Loading…</div>
        ) : (
          <div className="divide-y divide-[#e5e7eb]" data-testid="ts-users-list">
            {users.map((u) => (
              <div key={u.id} className="px-5 py-3 flex items-start gap-3" data-testid={`ts-user-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  {editing?.id === u.id ? (
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_180px] gap-2">
                      <Input value={editing.full_name || ""} onChange={(e) => setEditing((p) => ({ ...p, full_name: e.target.value }))}
                        className="bg-white border-[#e5e7eb] text-[13px]" data-testid="ts-edit-name" />
                      <Input value={editing.title || ""} placeholder="Title"
                        onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                        className="bg-white border-[#e5e7eb] text-[13px]" data-testid="ts-edit-title" />
                      <select value={editing.role || "contributor"} onChange={(e) => setEditing((p) => ({ ...p, role: e.target.value }))}
                        className="h-10 rounded-md border border-[#e5e7eb] bg-white px-2 text-[13px]"
                        data-testid="ts-edit-role">
                        <option value="contributor">Contributor</option>
                        <option value="tenant_admin">School Admin</option>
                      </select>
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold text-[14px] text-[#111827]">{u.full_name} <span className="text-[11px] font-normal text-[#6b7280]">· {u.email}</span></div>
                      <div className="text-[11px] text-[#6b7280] mt-0.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider mr-2 ${u.role === "tenant_admin" ? "bg-[#eff6ff] text-[#1e40af]" : "bg-[#f3f4f6] text-[#374151]"}`}>{(u.role || "").replace("_", " ")}</span>
                        {u.title || "—"}
                        {!u.is_active && <span className="ml-2 text-[10px] font-bold text-[#b91c1c] uppercase">deactivated</span>}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {editing?.id === u.id ? (
                    <>
                      <Button disabled={busy} onClick={saveEdit} className="bg-[#0b1220] text-white h-8 text-[12px]" data-testid="ts-edit-save">Save</Button>
                      <Button onClick={() => setEditing(null)} variant="outline" className="border-[#e5e7eb] h-8 text-[12px]" data-testid="ts-edit-cancel">Cancel</Button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing({ ...u })} className="text-[12px] font-semibold text-[#3B4FE4] hover:underline" data-testid={`ts-edit-${u.id}`}>Edit</button>
                      <button onClick={() => resetPw(u)} className="text-[12px] font-semibold text-[#a16207] hover:underline inline-flex items-center gap-1" data-testid={`ts-reset-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5" /> Reset password
                      </button>
                      <button onClick={() => { setEmailEdit(u); setNewEmail(u.email || ""); }}
                        className="text-[12px] font-semibold text-[#3B4FE4] hover:underline inline-flex items-center gap-1"
                        data-testid={`ts-email-${u.id}`}>
                        <Mail className="h-3.5 w-3.5" /> Email
                      </button>
                      {u.is_active ? (
                        <button onClick={() => deactivate(u)} className="text-[12px] font-semibold text-[#b91c1c] hover:underline inline-flex items-center gap-1" data-testid={`ts-deact-${u.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Deactivate
                        </button>
                      ) : (
                        <button onClick={() => reactivate(u)} className="text-[12px] font-semibold text-[#065f46] hover:underline" data-testid={`ts-react-${u.id}`}>Reactivate</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {users.length === 0 && <div className="p-6 text-[13px] text-[#6b7280]">No users yet.</div>}
          </div>
        )}
      </section>

      {/* Email-change modal (Phase 6.2) */}
      {emailEdit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setEmailEdit(null)} data-testid="ts-email-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#111827]">Change email — {emailEdit.full_name}</h3>
            <p className="text-[12px] text-[#6b7280] mt-1">Current: {emailEdit.email}</p>
            <p className="text-[11px] text-[#a16207] bg-[#fefce8] border border-[#fde047] rounded-md px-3 py-2 mt-3">
              Email changes are logged in the Audit tab. Email verification is not yet wired — only School Admins should edit emails until verification is enabled.
            </p>
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new.email@school.edu"
              className="mt-3 bg-white border-[#e5e7eb] text-[13px]"
              data-testid="ts-email-new" />
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailEdit(null)} className="border-[#e5e7eb]" data-testid="ts-email-cancel">Cancel</Button>
              <Button disabled={!newEmail.trim() || newEmail.trim().toLowerCase() === emailEdit.email}
                onClick={submitEmailChange}
                className="bg-[#007a9e] hover:bg-[#00607d] text-white"
                data-testid="ts-email-save">
                Update email
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Tab 2: Submission Date ── */
const SubmissionDateTab = () => {
  const [date, setDate] = useState("");
  const [original, setOriginal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/tenant-settings/submission-date`, { headers: getHdrs() });
      const v = r.data?.annual_submission_date || "";
      setDate(v); setOriginal(v);
    } catch { toast.error("Failed to load submission date"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!date) return toast.error("Pick a date");
    setBusy(true);
    try {
      await axios.put(`${API}/api/tenant-settings/submission-date`,
        { annual_submission_date: date }, { headers: getHdrs() });
      setOriginal(date);
      toast.success("Submission date saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-[#e5e7eb] bg-white p-6 max-w-2xl" data-testid="tenant-settings-date-tab">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-5 w-5 text-[#3B4FE4]" />
        <h2 className="font-bold text-[#111827]">Annual Submission Date</h2>
      </div>
      <p className="text-[13px] text-[#6b7280] mb-4">
        Set the date your school will submit its next annual report. The reminder cadence below will be timed against this date.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-white border-[#e5e7eb] text-[14px] max-w-[220px]"
          data-testid="ts-date-input"
        />
        <Button onClick={save} disabled={busy || date === original}
          className="bg-[#007a9e] hover:bg-[#00607d] text-white"
          data-testid="ts-date-save">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
      {original && (
        <p className="text-[12px] text-[#6b7280] mt-3">Currently set to <b className="text-[#111827]">{original}</b>.</p>
      )}
    </section>
  );
};

/* ── Tab 3: Reminders ── */
const REMINDER_LABELS = { 42: "6 weeks before", 28: "4 weeks before", 14: "2 weeks before", 7: "1 week before", 1: "1 day before" };

const RemindersTab = () => {
  const [cfg, setCfg] = useState({ enabled: true, offsets_days: [42,28,14,7,1], recipient_emails: [] });
  const [available, setAvailable] = useState([42,28,14,7,1]);
  const [submissionDate, setSubmissionDate] = useState("");
  const [recipientInput, setRecipientInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/tenant-settings/reminders`, { headers: getHdrs() });
      setCfg({
        enabled: r.data?.config?.enabled ?? true,
        offsets_days: r.data?.config?.offsets_days || [42,28,14,7,1],
        recipient_emails: r.data?.config?.recipient_emails || [],
      });
      setAvailable(r.data?.available_offsets || [42,28,14,7,1]);
      setSubmissionDate(r.data?.annual_submission_date || "");
    } catch { toast.error("Failed to load reminders"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/tenant-settings/reminders`, cfg, { headers: getHdrs() });
      toast.success("Reminder configuration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleOffset = (n) => {
    setCfg((p) => ({ ...p,
      offsets_days: p.offsets_days.includes(n)
        ? p.offsets_days.filter((x) => x !== n)
        : [...p.offsets_days, n].sort((a, b) => b - a),
    }));
  };

  const addRecipient = () => {
    const v = recipientInput.trim().toLowerCase();
    if (!v || !v.includes("@")) return toast.error("Enter a valid email");
    if (cfg.recipient_emails.includes(v)) return toast.error("Already added");
    setCfg((p) => ({ ...p, recipient_emails: [...p.recipient_emails, v] }));
    setRecipientInput("");
  };

  const removeRecipient = (e) => {
    setCfg((p) => ({ ...p, recipient_emails: p.recipient_emails.filter((x) => x !== e) }));
  };

  const runScan = async () => {
    try {
      const r = await axios.post(`${API}/api/tenant-settings/reminders/scan`, {}, { headers: getHdrs() });
      toast.success(`Scan complete — ${r.data?.sent || 0} reminder(s) queued.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
  };

  return (
    <section className="space-y-4 max-w-3xl" data-testid="tenant-settings-reminders-tab">
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-[#3B4FE4]" />
            <h2 className="font-bold text-[#111827]">Reminder Schedule</h2>
          </div>
          <label className="inline-flex items-center gap-2 text-[13px] font-semibold cursor-pointer" data-testid="ts-reminders-enabled-toggle">
            <input type="checkbox" checked={cfg.enabled}
              onChange={(e) => setCfg((p) => ({ ...p, enabled: e.target.checked }))}
              className="h-4 w-4" />
            Enabled
          </label>
        </div>
        <p className="text-[13px] text-[#6b7280] mb-3">
          Reminders fire at the selected offsets before <b className="text-[#111827]">{submissionDate || "(no submission date set)"}</b>. All reminders write to the notification log (email delivery currently MOCKED).
        </p>
        <div className="flex flex-wrap gap-2 mb-2" data-testid="ts-reminders-offsets">
          {available.map((n) => {
            const active = cfg.offsets_days.includes(n);
            return (
              <button key={n} onClick={() => toggleOffset(n)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                  active
                    ? "bg-[#3B4FE4] text-white border-[#3B4FE4]"
                    : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f3f4f6]"
                }`}
                data-testid={`ts-reminders-offset-${n}`}>
                {REMINDER_LABELS[n] || `${n}d before`}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-[#e5e7eb] bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-[#3B4FE4]" />
          <h2 className="font-bold text-[#111827]">Recipients</h2>
        </div>
        <p className="text-[13px] text-[#6b7280] mb-3">Up to 20 emails. Defaults to the tenant admin email when empty.</p>
        <div className="flex gap-2 mb-3">
          <Input placeholder="name@school.org" value={recipientInput}
            onChange={(e) => setRecipientInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); }}}
            className="bg-white border-[#e5e7eb] text-[13px]"
            data-testid="ts-reminders-recipient-input" />
          <Button onClick={addRecipient} variant="outline" className="border-[#e5e7eb]" data-testid="ts-reminders-recipient-add">Add</Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="ts-reminders-recipient-list">
          {cfg.recipient_emails.map((e) => (
            <span key={e} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f3f4f6] text-[12px] text-[#374151]">
              {e}
              <button onClick={() => removeRecipient(e)} className="text-[#6b7280] hover:text-[#b91c1c]" data-testid={`ts-reminders-rm-${e}`}>×</button>
            </span>
          ))}
          {cfg.recipient_emails.length === 0 && <span className="text-[12px] text-[#6b7280]">No recipients added — falls back to the school admin email.</span>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={busy}
          className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="ts-reminders-save">
          {busy ? "Saving…" : "Save reminder settings"}
        </Button>
        <Button onClick={runScan} variant="outline" className="border-[#e5e7eb] inline-flex items-center gap-1.5" data-testid="ts-reminders-scan">
          <RefreshCcw className="h-3.5 w-3.5" /> Run reminder scan now
        </Button>
      </div>
    </section>
  );
};

/* ── Tab 4: Audit (Phase 6.1) ── */
const AuditTab = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ actions: [], pillars: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", user_id: "", pillar: "", date_from: "", date_to: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("limit", "200");
      const r = await axios.get(`${API}/api/audit?${params.toString()}`, { headers: getHdrs() });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
      setFacets(r.data?.facets || { actions: [], pillars: [], users: [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load audit log"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await axios.get(`${API}/api/audit/export?${params.toString()}`, { headers: getHdrs(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `School-Health-Diagnostic-audit-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-[#9ca3af]">—</span>;
    if (typeof v === "object") return JSON.stringify(v).slice(0, 80);
    return String(v).slice(0, 120);
  };

  return (
    <div className="space-y-4" data-testid="tenant-settings-audit-tab">
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileSearch className="h-5 w-5 text-[#3B4FE4]" />
            <h2 className="font-bold text-[#111827]">Audit Trail</h2>
            <span className="text-[11px] text-[#6b7280]">{total} record{total !== 1 ? "s" : ""}</span>
          </div>
          <Button onClick={exportCsv} variant="outline" className="border-[#e5e7eb] inline-flex items-center gap-1.5" data-testid="ts-audit-export">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <select value={filters.action} onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value }))}
            className="h-9 rounded-md border border-[#e5e7eb] bg-white px-2 text-[12px]"
            data-testid="ts-audit-filter-action">
            <option value="">All actions</option>
            {facets.actions?.map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filters.user_id} onChange={(e) => setFilters((p) => ({ ...p, user_id: e.target.value }))}
            className="h-9 rounded-md border border-[#e5e7eb] bg-white px-2 text-[12px]"
            data-testid="ts-audit-filter-user">
            <option value="">All users</option>
            {facets.users?.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
          <select value={filters.pillar} onChange={(e) => setFilters((p) => ({ ...p, pillar: e.target.value }))}
            className="h-9 rounded-md border border-[#e5e7eb] bg-white px-2 text-[12px]"
            data-testid="ts-audit-filter-pillar">
            <option value="">All pillars</option>
            {facets.pillars?.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
            className="h-9 bg-white border-[#e5e7eb] text-[12px]" data-testid="ts-audit-filter-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
            className="h-9 bg-white border-[#e5e7eb] text-[12px]" data-testid="ts-audit-filter-to" />
        </div>
      </div>

      <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-[13px] text-[#6b7280]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-[13px] text-[#6b7280]">No audit records match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="ts-audit-table">
              <thead className="bg-[#f9fafb] border-b border-[#e5e7eb]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">
                  <th className="px-3 py-2">Date / Time</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Pillar / Question</th>
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Previous</th>
                  <th className="px-3 py-2">New</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e7eb]">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-[#f9fafb]" data-testid={`ts-audit-row-${r.id}`}>
                    <td className="px-3 py-2 text-[#374151] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <div className="text-[#111827] font-medium">{r.user_name || "—"}</div>
                      <div className="text-[10px] text-[#6b7280]">{r.user_email} · {r.user_role}{r.user_job_title ? ` · ${r.user_job_title}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-[#111827] font-semibold whitespace-nowrap">{(r.action_type || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-[#374151]">
                      {r.pillar && <div>{r.pillar}</div>}
                      {r.question_text && <div className="text-[10px] text-[#6b7280] truncate max-w-[260px]" title={r.question_text}>{r.question_text}</div>}
                      {!r.pillar && !r.question_text && <span className="text-[#9ca3af]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[#374151]">{fmtVal(r.field_changed)}</td>
                    <td className="px-3 py-2 text-[#6b7280]">{fmtVal(r.previous_value)}</td>
                    <td className="px-3 py-2 text-[#111827]">{fmtVal(r.new_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-[11px] text-[#6b7280]">Audit records are read-only and retained for governance and compliance. Filter and export as needed.</p>
    </div>
  );
};

export default TenantSettingsPage;
